import {
  HOMES_TARGETED_URL_PROPERTY_TYPE,
  REAL_ESTATE_TARGETED_URL_PROPERTY_TYPE,
  getTargetedUrlPropertyTypeSelection,
  isForSaleOrRent,
} from 'utils/listing-query-helper';
import { capitalizeWords } from '@zoocasa/node-kit/strings/capitalize';
import { AVAILABLE_STATUS, Filter, NOT_AVAILABLE_SOLD_STATUS, PENDING_STATUS } from 'contexts/preferences/listing-params/types';
import { AddressType } from '@zoocasa/go-search';

/**
 * Generates a formatted heading for the area listings page based on various filter criteria and area information.
 *
 * The function creates different heading formats depending on:
 * 1. Property status (Available, Sold, Rental)
 * 2. Property type (House, Townhouse, Condo, Farm, Commercial, Land, or general Real Estate)
 * 3. Area name
 * 4. Whether it's a rental or sale listing
 *
 * The heading logic follows these rules:
 * - For available properties:
 *   Format: "[Property Type] [For Sale/For Rent] in [Area Name]"
 *   Example: "Houses For Sale in Downtown Toronto"
 *
 * - For sold/rented properties:
 *   Format: "[Area Name] Recently [Sold/Rented] [Property Type]"
 *   Example: "Downtown Toronto Recently Sold Houses"
 *
 * - For pending or expired properties:
 *   Format: "[Area Name] [Pending/Past] [Property Type]{ for Rent}"
 *   Example: "Downtown Toronto Pending Condos", "Downtown Toronto Past Condos for Rent"
 *
 * - When no listings are found (fallback address):
 *   Appends "/ 0 Listings Found" to the end of the heading
 *
 * The property type is determined by checking the homeType filter object:
 * - Single property type selected: Uses specific type (Houses, Townhouses, Condos, etc.)
 * - Multiple types or none selected: Uses "Real Estate"
 * 
 * The city and neighbourhood are optional and are used to determine if we are on a neighbourhood page. If so,
 * the [Area Name] will be formatted as "[Neighbourhood], [City]"
 *
 * @param areaName The name of the geographical area for the listings (e.g., "Downtown Toronto")
 * @param filter An object containing filter criteria:
 *   - rental Boolean indicating if these are rental listings
 *   - status Current status of the listings (AVAILABLE_STATUS, NOT_AVAILABLE_SOLD_STATUS, etc.)
 *   - homeType Object containing boolean flags for different property types: house, townhouse, condo, farm, commercial, land
 * @param isFallbackArea Boolean indicating if no listings were found for the original search criteria
 * @param city The city of the geographical area
 * @param neighbourhood The neighbourhood of the geographical area
 *
 * @returns A formatted string representing the page heading with proper capitalization
 *
 * @example
 * // Available properties
 * generateHeading("Downtown Toronto", { rental: false, status: "Available", homeType: { house: true } })
 * // Returns: "Houses For Sale in Downtown Toronto"
 *
 * @example
 * // Sold properties
 * generateHeading("Downtown Toronto", { rental: false, status: "Sold", homeType: { condo: true } })
 * // Returns: "Downtown Toronto Recently Sold Condos"
 *
 * @example
 * // No listings found -- "0 Listings Found" will be added to the title below this line in the front-end
 * generateHeading("Downtown Toronto", { rental: false, status: "Available", homeType: { house: true } })
 * // Returns: "Houses For Sale in Downtown Toronto"
 */
interface GenerateHeadingParams {
  areaName: string;
  areaType: AddressType;
  filter: Pick<Filter, 'rental' | 'status' | 'homeType'>;
  city?: string;
  neighbourhood?: string;
}

/*eslint max-params: ["error", 1]*/
export default function generateHeading({ areaName, areaType, filter, city, neighbourhood }: GenerateHeadingParams) {
  const forSaleOrRent = isForSaleOrRent(filter.rental);
  const homeTypeSelection = getTargetedUrlPropertyTypeSelection(filter.homeType);
  let homeType = homeTypeSelection === HOMES_TARGETED_URL_PROPERTY_TYPE ? REAL_ESTATE_TARGETED_URL_PROPERTY_TYPE : homeTypeSelection;

  if (areaType !== AddressType.ADDRESS_TYPE_COUNTRY && homeTypeSelection === HOMES_TARGETED_URL_PROPERTY_TYPE) {
    homeType += ` & ${HOMES_TARGETED_URL_PROPERTY_TYPE}`;
  }

  let areaNameText = areaName;
  if (city && neighbourhood) {
    areaNameText = `${neighbourhood}, ${city}`;
  }

  if (filter.status !== AVAILABLE_STATUS) {
    // Sold keeps its "Recently Sold"/"Recently Rented" phrasing.
    if (filter.status === NOT_AVAILABLE_SOLD_STATUS) {
      return capitalizeWords(`${areaNameText} Recently ${filter.rental ? 'Rented' : 'Sold'} ${homeType}`);
    }
    // Pending and expired describe the listing's current state, and both name the home type
    // so the heading still matches the applied filter ("Toronto Pending Condos"). Pending used
    // to collapse into "Past Listings", which called a live listing a past one.
    const state = filter.status === PENDING_STATUS ? 'Pending' : 'Past';
    // `homeType` is a plural noun phrase ("Condos", "Real Estate & Homes"), so rentals take a
    // trailing "for Rent" rather than a "Rental" adjective that would not agree with it. The
    // previous "Recently Rented" here wrongly claimed a pending or expired unit was rented.
    const rentalSuffix = filter.rental ? ' for Rent' : '';
    return capitalizeWords(`${areaNameText} ${state} ${homeType}${rentalSuffix}`);
  } else {
    let message: string;
    if (areaName) {
      message = `${homeType} ${forSaleOrRent} in ${areaNameText}`;
    } else {
      message = `${homeType} ${forSaleOrRent}`;
    }

    return capitalizeWords(message);
  }
}
